/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kRetryCore — server-safe retry logic with exponential backoff.
 *
 * Provides:
 *   kRetryCore — static class with transient-error detection, Retry-After
 *                parsing, and exponential backoff + jitter calculation.
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import { getCoreUtils } from "./kCoreHandler";

// ── Transient HTTP status codes that warrant a retry ─────────────────────────
// 429 throttle + the three "upstream/gateway momentarily unavailable" codes: 502 Bad
// Gateway, 503 Service Unavailable, 504 Gateway Timeout. 502 belongs with 503/504 — a
// proxy/gateway returning an invalid/incomplete upstream response is a transient blip
// that a bounded retry recovers (RUNTIME-CONFIRMED 2026-07-10: ALSO GetSessionToken 502'd
// mid-cycle; it was previously omitted, so neither the api-layer reads nor the token mint
// retried it and a brief blip halted a whole sync cycle).

const TransientStatusCodes: Set<number> = new Set([429, 502, 503, 504]);

// ── Transient TRANSPORT error codes (a string code, not an HTTP status) ───────
// A request can fail BEFORE any HTTP status exists, so a status-only check
// (IsTransient) sees 0 and wrongly treats the blip as fatal. Two families:
//
// AUTH. MSAL token acquisition (e.g. acquireTokenWithCachedRefreshToken) can fail
// at the transport level — the POST to the token endpoint never completed: a
// network blip, the browser momentarily offline, or a proxy / extension dropping
// the request. These surface as a BrowserAuthError whose `errorCode` is one of the
// codes below. RUNTIME-CONFIRMED 2026-07-23: a langate user hit
// `post_request_failed` on the sign-in sync's silent token refresh and, because
// it has no HTTP status, GraphPagingClient hard-failed it straight to the global
// error modal instead of retrying. Interactive-required codes (interaction_
// required / login_required / consent_required) are DELIBERATELY excluded: they
// need the user, so retrying them silently is pointless and they must still surface.
//
// TIMEOUT (`aborterror`). Every abort in this estate is a TIMEOUT, never a caller
// cancellation — kFunctionGraphClient's TimeoutMiddleware (60 s per Graph request),
// kGraphCore's kGraphFetchOperation.RequestTimeoutMs, and kCertTokenJob.OnTimeout
// each fire an `OnTimeout` that aborts a request nobody cancelled. The SDK reports
// it as a GraphError with `statusCode = -1` and `code = error.name` = "AbortError"
// (GraphErrorHandler.constructError), which matched neither set — so a stalled
// request went straight to the global handler. RUNTIME-CONFIRMED 2026-08-12: CTS
// Sentinel's SurgeStage aborted one `/auditLogs/signIns` page and reported
// `GraphSignInClient._OnNonTransient — This operation was aborted`, killing the
// stage for that tick. It was NOT a capacity problem: that same window measures
// 604 sign-ins over 3 pages, slowest page 2.5 s — 4 % of the 60 s budget — i.e. a
// momentary Graph-side stall, the textbook case for a bounded retry. Each attempt
// builds a FRESH AbortController + timer, so the retry is a genuine new request.
const TransientTransportCodes: Set<string> = new Set([
    "post_request_failed",
    "no_network_connectivity",
    "network_error",
    "endpoints_resolution_error",
    "aborterror",
    // A FETCH rejection is a DOMException whose `code` is NUMERIC (20 AbortError, 23 TimeoutError),
    // so _ErrorCode reads its `name` instead (2026-10-09). "timeouterror" is what AbortSignal.timeout
    // rejects with (NodeRestExecutor, kBearerRestExecutor, kBotConnector) - also our own timeout.
    "timeouterror",
    // undici's own transport TypeErrors carry no code, only a message (2026-10-22, kErrors from MOSE,
    // ExternalUsers, iso-cert-tool, CTS Sentinel): "fetch failed" = the request never completed
    // (reset/DNS/TLS), "terminated" = the socket closed while the BODY was being read.
    "fetch failed",
    "terminated"
]);

// ── kRetryCore ───────────────────────────────────────────────────────────────

export class kRetryCore {

    public static MaxRetries: number = 3;
    public static BaseDelayMs: number = 1000;

    // ── Transient detection ────────────────────────────────────────────

    public static IsTransient = (StatusCode: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return TransientStatusCodes.has(StatusCode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.IsTransient");
            return false;
        }
    };

    /**
     * True when an HTTP status says the REQUEST ITSELF is refused, so sending it again cannot help
     * (no permission, name taken, too large, bad request): every 4xx except 408 (request timeout)
     * and 429 (throttle), plus 507 (insufficient storage). 0, 5xx other than 507, 408 and 429 are
     * NOT permanent. This is the complement the transient list does not give: a status can be
     * neither transient nor permanent (500, 501), which a caller may treat as "retry a bounded
     * number of times". Lifted from DeckBuilder's DECKBSPVideoUpload.IsRefusal (2026-10-05); a
     * second consumer changes nothing - it only calls this.
     */
    public static IsPermanentRefusal = (StatusCode: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (StatusCode === 507) { return true; }
            if (StatusCode === 408 || StatusCode === 429) { return false; }
            return StatusCode >= 400 && StatusCode < 500;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.IsPermanentRefusal");
            return false;
        }
    };

    // True when a whole ERROR OBJECT is worth a bounded retry — either it carries
    // a transient HTTP status (429/502/503/504) OR it is a transient TRANSPORT
    // failure that never reached an HTTP status at all: a MSAL token POST that did
    // not complete, or a request the caller's own timeout aborted. The status-only
    // IsTransient misses the latter (it sees 0 / -1), so callers holding the error
    // object should prefer this. Retriers that only have the status code keep
    // using IsTransient.
    public static IsTransientError = (Error: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (kRetryCore.IsTransient(kRetryCore.ExtractStatusCode(Error)) === true) {
                return true;
            }
            return TransientTransportCodes.has(kRetryCore._ErrorCode(Error));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.IsTransientError");
            return false;
        }
    };

    // MaxRetries defaults to the shared class budget; callers with their own
    // budget (e.g. an interactive chat that must fail fast) pass it explicitly
    // WITHOUT mutating the shared static that other callers depend on.
    public static ShouldRetry = (StatusCode: number, Attempt: number, MaxRetries: number = kRetryCore.MaxRetries): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Attempt >= MaxRetries) { return false; }
            return TransientStatusCodes.has(StatusCode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.ShouldRetry");
            return false;
        }
    };

    // ── Status code extraction ─────────────────────────────────────────
    // Handles multiple property naming conventions across SP REST,
    // Graph API, and fetch responses.

    public static ExtractStatusCode = (Error: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (!Error) { return 0; }
            if (typeof Error.StatusCode === "number") { return Error.StatusCode; }
            if (typeof Error.statusCode === "number") { return Error.statusCode; }
            if (typeof Error.status === "number") { return Error.status; }
            return 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.ExtractStatusCode");
            return 0;
        }
    };

    // Extract a lower-cased error CODE string from a rejected auth/transport error.
    // Prefers MSAL's `errorCode`, then a generic `code`, then the leading token of
    // `message` (MSAL sets message to "<errorCode>: <description>"). Used by
    // IsTransientError to spot a transient BrowserAuthError that carries no status.
    private static _ErrorCode = (Error: any): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (!Error) { return ""; }
            if (typeof Error.errorCode === "string" && Error.errorCode !== "") {
                return Error.errorCode.toLowerCase();
            }
            if (typeof Error.code === "string" && Error.code !== "") {
                return Error.code.toLowerCase();
            }
            if (Error.name === "AbortError" || Error.name === "TimeoutError") {
                return Error.name.toLowerCase();
            }
            let Message: string = "";
            if (typeof Error.message === "string") { Message = Error.message; }
            if (Message === "") { return ""; }
            let Head: string = Message;
            const ColonPos: number = Message.indexOf(":");
            if (ColonPos > 0) { Head = Message.substring(0, ColonPos); }
            return Head.trim().toLowerCase();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore._ErrorCode");
            return "";
        }
    };

    // ── Retry delay calculation ────────────────────────────────────────

    public static GetRetryDelay = (Error: any, Attempt: number): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const RetryAfterSec: number = kRetryCore.ParseRetryAfter(Error);
            if (RetryAfterSec > 0) {
                return RetryAfterSec * 1000;
            }
            return kRetryCore.CalculateBackoff(Attempt);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.GetRetryDelay");
            return kRetryCore.BaseDelayMs;
        }
    };

    // ── Exponential backoff with jitter ─────────────────────────────────

    // BaseDelayMs / MaxDelayMs default to the shared budget; callers may pass a
    // tighter base and a hard ceiling (e.g. a chat capping the spinner wait)
    // without touching the shared statics. MaxDelayMs <= 0 means no ceiling.
    public static CalculateBackoff = (Attempt: number, BaseDelayMs: number = kRetryCore.BaseDelayMs, MaxDelayMs: number = 0, JitterMs: number = 500): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const ExponentialMs: number = BaseDelayMs * Math.pow(2, Attempt);
            let Jitter: number = 0;
            if (JitterMs > 0) { Jitter = Math.floor(Math.random() * JitterMs); }
            let Total: number = ExponentialMs + Jitter;
            if (MaxDelayMs > 0 && Total > MaxDelayMs) { Total = MaxDelayMs; }
            return Total;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.CalculateBackoff");
            return BaseDelayMs;
        }
    };

    // ── Retry-After header parsing ──────────────────────────────────────
    // SP and Graph may include Retry-After in the response body or headers.
    // RestResponseError stores response text which may contain the header.

    // The HEADER comes first: RestResponseError.RetryAfter carries it since 2026-09-24 (the browser
    // executor used to drop it, so the text scan below never saw a throttle's own wait). Seconds only -
    // an HTTP-date form falls through to the text scan and then to backoff, as before.
    public static ParseRetryAfter = (Error: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (!Error) { return 0; }
            if (Error.RetryAfter !== undefined && Error.RetryAfter !== null && String(Error.RetryAfter).trim() !== "") {
                const HeaderSeconds: number = Number(String(Error.RetryAfter).trim());
                if (isFinite(HeaderSeconds) && HeaderSeconds > 0 && HeaderSeconds <= 120) { return Math.ceil(HeaderSeconds); }
            }
            const ResponseText: string = Error.ResponseText || Error.responseText || "";
            if (ResponseText === "") { return 0; }
            const RetryMatch: RegExpMatchArray | null = ResponseText.match(/Retry-After[:\s]+(\d+)/i);
            if (RetryMatch) {
                const Seconds: number = parseInt(RetryMatch[1], 10);
                if (Seconds > 0 && Seconds <= 120) { return Seconds; }
            }
            return 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.ParseRetryAfter");
            return 0;
        }
    };

    // ── Is throttle (429) ───────────────────────────────────────────────

    public static IsThrottle = (StatusCode: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return StatusCode === 429;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.IsThrottle");
            return false;
        }
    };

    // A transport failure known only as TEXT (e.g. kMcpToolResult.Error "winmcp could not be reached: socket hang up")
    // that shows the connection was dropped or refused - the server restarted. Timeouts and aborts are NOT included:
    // callers use it to rerun a whole long call once, which a timeout must not trigger.
    public static TextSaysConnectionLost = (Detail: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Text: string = Detail.toLowerCase();
            if (Text.indexOf("socket hang up") >= 0) { return true; }
            if (Text.indexOf("econnreset") >= 0) { return true; }
            if (Text.indexOf("econnrefused") >= 0) { return true; }
            if (Text.indexOf("epipe") >= 0) { return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kRetryCore.TextSaysConnectionLost");
            return false;
        }
    };
}
